<script setup>
const config = useRuntimeConfig()
let clearId = null
const map = shallowRef(null)
const mapContainer = ref(null)
const imgElementRef = ref(null)
const targetElement = ref(null)
const imgVisible = ref(true)
const { data: currentList } = await useFetch('/api/river/current/all')

const cameraIndex = ref(0)
const selectedQuery = computed(() => {
  return currentList?.value?.[cameraIndex.value]?.query ?? ''
})
const mapCenter = computed(() => {
  return currentList?.value?.[cameraIndex.value]?.center ?? [0, 0]
})
const pointRank = computed(() => {
  return currentList?.value?.[cameraIndex.value]?.rank ?? '0'
})
const { data: pointData } = await useFetch(computed(() => `https://aplapi.weathernews.jp/lba/river/api_observatory_detail${selectedQuery.value}`))

const sliderValue = ref(0)
const imgUrl = computed(() => {
  if (!pointData.value || !Array.isArray(pointData.value.camera_list) || pointData.value.camera_list.length === 0) return ''
  return pointData.value.camera_list[sliderValue.value]?.url ?? 'no-image'
})

watch(selectedQuery, () => {
  sliderValue.value = 0
  clearTimeout(clearId)
  clearId = setTimeout(anime, 1250)
  imgVisible.value = true
})

watch(() => mapCenter.value, () => {
  if (map.value) {
    map.value.jumpTo({ center: mapCenter.value })
  }
})

const nextCamera = () => {
  if (cameraIndex.value === currentList.value.length - 1) {
    cameraIndex.value = 0
  } else {
    cameraIndex.value++
  }
}
const anime = () => {
  if (!pointData.value || !Array.isArray(pointData.value.camera_list) || pointData.value.camera_list.length === 0) {
    clearTimeout(clearId)
    return
  }
  if (sliderValue.value < pointData.value.camera_list.length - 1) {
    sliderValue.value++
  } else {
    nextCamera()
  }
  if (sliderValue.value === pointData.value.camera_list.length - 1) {
    clearId = setTimeout(anime, 2000)
    setTimeout(() => {
      imgVisible.value = false
    }, 1850)
  } else {
    clearId = setTimeout(anime, 1000)
  }
}
const onClickPlay = () => {
  clearTimeout(clearId)
  clearId = setTimeout(anime, 1000)
}
const onClickStop = () => {
  clearTimeout(clearId)
}
const onClickImage = () => {
  navigateTo(currentList.value[cameraIndex.value].path)
}
const onClickLink = (index) => {
  if (index === cameraIndex.value) {
    navigateTo(currentList.value[index].path)
  } else {
    cameraIndex.value = index
  }
}

onUnmounted(() => {
  if (targetElement.value) {
    observer.unobserve(targetElement.value)
  }
  clearTimeout(clearId)
})
onMounted(async () => {
  map.value = await initBasicMap(mapContainer.value, mapCenter.value)
  const observerOptions = {
    root: null,
    threshold: 1
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        onClickPlay()
      } else {
        onClickStop()
      }
    })
  }, observerOptions)

  if (targetElement.value) {
    observer.observe(targetElement.value)
  }
})
</script>

<template>
  <div class="content">
    <div
      ref="targetElement"
      class="box"
    >
      <figure
        class="image"
        :data-rank="pointRank"
        @click="onClickImage"
      >
        <img
          v-if="imgUrl && imgUrl !== 'no-image'"
          ref="imgElementRef"
          :src="imgUrl"
          alt=""
          :class="{ visible: imgVisible }"
        >
        <div
          v-if="imgUrl==='no-image'"
          ref="imgElementRef"
          class="no-image"
        >
          <p>カメラ画像が取得できませんでした。</p>
        </div>
      </figure>
      <div
        ref="mapContainer"
        class="map"
      />
      <div class="icon">
        <img
          :src="`${config.public.imgDomain}/onebox/img/river/map/livecam_wni.png`"
          alt=""
        >
      </div>
    </div>
    <div class="block">
      <div
        v-for="(item, index) in currentList"
        :key="index"
        :data-selected="item.query === selectedQuery"
        class="link"
        @click="onClickLink(index)"
      >
        <p
          v-if="setRankText[item.rank]"
          class="rank"
          :data-rank="item.rank"
        >
          {{ setRankDetailText[item.rank] }}
        </p>
        <div class="text-box">
          <p class="info">
            {{ item.info }}
          </p>
          <p class="text">
            {{ item.name }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .box {
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: #EEF1F8;
    max-width: 100%;
    .image {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: calc((100vw - 20px) * 9 / 16);
      aspect-ratio: 16 / 9;
      &[data-rank="5"] {
        border: 5px solid #0C000C;
      }
      &[data-rank="4"] {
        border: 5px solid #A0A;
      }
      &[data-rank="3"] {
        border: 5px solid #FF2800;
      }
      &[data-rank="2"] {
        border: 5px solid #F2E700;
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        opacity: 0;
        transition: opacity 0.15s linear;
        &.visible {
          opacity: 1;
          transition: opacity 0.25s linear;
        }
      }
      .no-image {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #EEF1F8;
        p {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #ff3f00;
          font-size: 16px;
          font-weight: 600;
          line-height: 1em;
        }
      }
    }
    .map {
      position: absolute;
      bottom: 6px;
      left: 6px;
      width: 60px;
      height: 60px;
      pointer-events: none;
      figure {
        width: 100%;
        height: 100%;
        img {
          width: 100%;
          height: 100%;
        }
      }
    }
    .icon {
      position: absolute;
      bottom: 28.5px;
      left: 28.5px;
      width: 15px;
      height: 15px;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .block {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 5px;
    max-height: 183px;
    overflow-y: scroll;
    padding: 6px 10px;
    border: 1px solid #CCC;
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background: #EEF1F8;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #438FEC;
    }
    .link {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      padding: 5px 14px 5px 6px;
      border-bottom: 1px solid #CCC;
      cursor: pointer;
      &[data-selected="true"] {
        background-color: #EEF1F8;
        background-image: url('https://gvs.weathernews.jp/onebox/img/common/icon_arrow_button_tall.svg');
        background-position: right 1px center;
        background-repeat: no-repeat;
        background-size: 13px;
        .text {
          color: #303030;
        }
      }
      .text-box{
        display: flex;
        flex-direction: column;
        width: 100%;
        .info {
          color: #303030;
          font-size: 12px;
          font-weight: 300;
          line-height: 12px;
          max-width: 100%;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .text {
          color: #3569C0;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
        }
      }
      .rank {
        padding: 3px 5px;
        color: #FFF;
        font-size: 10px;
        font-weight: 600;
        line-height: 1em;
        border-radius: 2px;
        background-color: #FFF;
        max-width: 100%;
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        &[data-rank="5"] {
          background-color: #0C000C;
        }
        &[data-rank="4"] {
          background-color: #A0A;
        }
        &[data-rank="3"] {
          background-color: #FF2800;
        }
        &[data-rank="2"] {
          background-color: #F2E700;
          color: #303030;
        }
      }
    }
  }
}
@media (any-hover: hover) {
  .content {
    .image {
      transition: opacity .2s ease-in-out;
      cursor: pointer;
      &:hover {
        opacity: .6;
      }
    }
    .block {
      .link {
        &:not([data-selected="true"]) {
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .content {
    flex-direction: row;
    gap: 10px;
    min-height: 274px;
    .box {
      max-width: 64%;
      .image {
        height: 274px;
        img {
          object-fit: contain;
          vertical-align: bottom;
        }
      }
    }
    .block {
      max-height: 274px;
      padding: 10px;
      .link {
        .text-box {
          p {
            font-size: 15px;
          }
        }
      }
    }
  }
}
</style>
